import { Link } from "react-router-dom";
import type { CSSProperties, ReactNode } from "react";
import { Section } from "./Section";
import type { CmsBlock, CmsPage as CmsPageType } from "../data/cms";
import { CampaignDashboard } from "./CampaignDashboard";

type CmsButtonLike = { label: string; href: string };
type ResolveCmsHref = (button: CmsButtonLike) => string;
type TrackCmsButton = (button: CmsButtonLike, context: string, resolvedHref: string) => void;
type TrackRequestInfo = (action: "schedule_visit" | "apply", label: string) => void;

function paragraphs(body?: string) {
  return String(body ?? "")
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function isExternalHref(href: string) {
  return /^https?:\/\//i.test(href);
}

function defaultResolveCmsHref(button: CmsButtonLike) {
  const normalized = button.label.toLowerCase();
  if (button.href === "/admissions" && normalized.includes("schedule") && normalized.includes("visit")) return "/contact";
  return button.href;
}

function noopTrackButton() {}
function noopTrackRequestInfo() {}

function CmsLink({
  href,
  children,
  className,
  onClick,
  openInNewTab,
  preview = false,
}: {
  href: string;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  openInNewTab?: boolean;
  preview?: boolean;
}) {
  const safeHref = href || "/";
  if (preview || isExternalHref(safeHref)) {
    const shouldOpenNewTab = openInNewTab ?? (safeHref.startsWith("http://") || safeHref.startsWith("https://"));
    return (
      <a className={className} href={safeHref} onClick={onClick} target={shouldOpenNewTab ? "_blank" : undefined} rel={shouldOpenNewTab ? "noreferrer" : undefined}>
        {children}
      </a>
    );
  }

  return (
    <Link className={className} to={safeHref} onClick={onClick}>
      {children}
    </Link>
  );
}

function CmsButtonLink({
  href,
  children,
  variant = "primary",
  onClick,
  openInNewTab,
  preview = false,
}: {
  href: string;
  children: ReactNode;
  variant?: "primary" | "secondary" | "outline" | "gold";
  onClick?: () => void;
  openInNewTab?: boolean;
  preview?: boolean;
}) {
  return (
    <CmsLink className={`button button-${variant}`} href={href} onClick={onClick} openInNewTab={openInNewTab} preview={preview}>
      {children}
    </CmsLink>
  );
}

function renderButtons(block: CmsBlock, resolveCmsHref: ResolveCmsHref, onButtonClick: TrackCmsButton, preview = false) {
  if (!block.buttons?.length) return null;
  return (
    <div className="section-actions">
      {block.buttons.map((button, index) => {
        const href = resolveCmsHref(button);
        return (
          <CmsButtonLink key={`${button.href}-${index}`} href={href} variant={button.variant ?? "primary"} openInNewTab={button.openInNewTab ?? isExternalHref(href)} onClick={() => onButtonClick(button, block.title, href)} preview={preview}>
            {button.label}
          </CmsButtonLink>
        );
      })}
    </div>
  );
}

function blockStyleClass(block: CmsBlock) {
  const style = block.style || {};
  return [
    style.fontFamily ? `cms-font-${style.fontFamily}` : "",
    style.sectionHeight ? `cms-height-${style.sectionHeight}` : "",
    style.paddingTop ? `cms-pad-top-${style.paddingTop}` : "",
    style.paddingBottom ? `cms-pad-bottom-${style.paddingBottom}` : "",
    style.contentWidth ? `cms-width-${style.contentWidth}` : "",
  ].filter(Boolean).join(" ");
}

function blockInlineStyle(block: CmsBlock): CSSProperties {
  const style = block.style || {};
  return {
    "--cms-block-bg": style.backgroundColor || undefined,
    "--cms-eyebrow-color": style.eyebrowColor || undefined,
    "--cms-heading-color": style.headingColor || undefined,
    "--cms-body-color": style.bodyColor || undefined,
  } as CSSProperties;
}

function youtubeEmbedUrl(value?: string) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  const match = trimmed.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : "";
}

function VideoBlock({ block }: { block: CmsBlock }) {
  const embedUrl = youtubeEmbedUrl(block.videoUrl);
  const autoplay = Boolean(block.videoAutoplayMuted && block.videoUrl && !embedUrl);
  return (
    <div className={["cms-video", block.videoDisplay === "playerCard" ? "cms-video-card" : ""].filter(Boolean).join(" ")}>
      {embedUrl ? (
        <iframe
          src={embedUrl}
          title={block.title || "Video"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      ) : block.videoUrl ? (
        <video src={block.videoUrl} controls={!autoplay} autoPlay={autoplay} muted={autoplay} loop={autoplay} playsInline={autoplay} />
      ) : (
        <div className="cms-video-placeholder">Video coming soon.</div>
      )}
      {block.videoCaption ? <p className="caption">{block.videoCaption}</p> : null}
    </div>
  );
}

function ImageTextBlock({ block, resolveCmsHref, onButtonClick, preview }: { block: CmsBlock; resolveCmsHref: ResolveCmsHref; onButtonClick: TrackCmsButton; preview?: boolean }) {
  return (
    <div className="cms-image-text">
      <figure>
        {block.imageUrl ? (
          <img src={block.imageUrl} alt={block.imageAlt || block.title || ""} loading="lazy" />
        ) : (
          <div className="cms-image-placeholder">Image coming soon.</div>
        )}
        {block.imageCaption ? <figcaption>{block.imageCaption}</figcaption> : null}
      </figure>
      <div>
        {paragraphs(block.body).map((part, index) => <p key={part} className={index === 0 ? "lead" : undefined}>{part}</p>)}
        {renderButtons(block, resolveCmsHref, onButtonClick, preview)}
      </div>
    </div>
  );
}

function GalleryBlock({ block }: { block: CmsBlock }) {
  const cards = (block.cards || []).filter((card) => card.imageUrl || card.title || card.text);
  return (
    <div className="cms-gallery-grid">
      {cards.map((card, index) => (
        <figure key={`${card.imageUrl || card.title}-${index}`}>
          {card.imageUrl ? <img src={card.imageUrl} alt={card.imageAlt || card.title || ""} loading="lazy" /> : <div className="cms-image-placeholder">Image coming soon.</div>}
          {card.title || card.text ? (
            <figcaption>
              {card.title ? <strong>{card.title}</strong> : null}
              {card.text ? <span>{card.text}</span> : null}
            </figcaption>
          ) : null}
        </figure>
      ))}
    </div>
  );
}

function SlideshowBlock({ block, preview }: { block: CmsBlock; preview?: boolean }) {
  const slides = (block.cards || []).filter((card) => card.imageUrl || card.title || card.text);
  if (!slides.length) return <div className="cms-image-placeholder">Slides coming soon.</div>;
  return (
    <div className="cms-slideshow" aria-label={block.title || "Photo slideshow"}>
      {slides.map((slide, index) => (
        <article key={`${slide.imageUrl || slide.title}-${index}`} className="cms-slide">
          {slide.imageUrl ? <img src={slide.imageUrl} alt={slide.imageAlt || slide.title || ""} loading={index === 0 ? "eager" : "lazy"} /> : <div className="cms-image-placeholder">Image coming soon.</div>}
          {(slide.title || slide.text || slide.href) ? (
            <div className="cms-slide-copy">
              {slide.title ? <h3>{slide.title}</h3> : null}
              {slide.text ? <p>{slide.text}</p> : null}
              {slide.href ? <CmsLink href={slide.href} preview={preview}>Learn more</CmsLink> : null}
            </div>
          ) : null}
        </article>
      ))}
    </div>
  );
}

function NewsEventsBlock({ preview = false }: { preview?: boolean }) {
  return (
    <div className="split-list">
      <div>
        <h3>Latest News</h3>
        <ul className="clean-list">
          <li><CmsLink href="/news" preview={preview}>View school news</CmsLink></li>
        </ul>
      </div>
      <div>
        <h3>Upcoming Events</h3>
        <ul className="clean-list">
          <li><CmsLink href="/calendar" preview={preview}>View school calendar</CmsLink></li>
        </ul>
      </div>
    </div>
  );
}

function PagePhotoHero({ page }: { page: CmsPageType }) {
  return (
    <section className="page-photo-hero">
      {page.heroVideoUrl ? (
        <video src={page.heroVideoUrl} autoPlay muted loop playsInline aria-hidden="true" />
      ) : (
        <img src={page.heroImageUrl} alt={page.heroImageAlt || ""} />
      )}
      <div className="page-photo-hero-overlay">
        <div className="container">
          <p className="eyebrow">Zion Classical Academy</p>
          <h1>{page.title}</h1>
          {page.seoDescription ? <p>{page.seoDescription}</p> : null}
        </div>
      </div>
    </section>
  );
}

function RequestInfoBand({ slug, onRequestInfoClick, preview }: { slug: string; onRequestInfoClick: TrackRequestInfo; preview?: boolean }) {
  if (["home", "contact", "parents", "support", "giving"].includes(slug)) return null;
  return (
    <section className="request-info-band">
      <div className="container request-info-inner">
        <div>
          <p className="eyebrow">Take the next step</p>
          <h2>Ready to learn whether ZCA is right for your family?</h2>
          <p>Schedule a visit, ask admissions questions, or request more information from the school office.</p>
        </div>
        <div className="request-info-actions">
          <CmsButtonLink href="/contact" variant="gold" onClick={() => onRequestInfoClick("schedule_visit", `Request Info Band: ${slug}`)} preview={preview}>
            Request Information
          </CmsButtonLink>
          <CmsButtonLink href="/admissions" variant="outline" onClick={() => onRequestInfoClick("apply", `Request Info Band: ${slug}`)} preview={preview}>
            Admissions Process
          </CmsButtonLink>
        </div>
      </div>
    </section>
  );
}

type BlockProps = {
  block: CmsBlock;
  slug: string;
  heroImageUrl?: string;
  heroVideoUrl?: string;
  newsEventsContent?: ReactNode;
  resolveCmsHref: ResolveCmsHref;
  onButtonClick: TrackCmsButton;
  preview?: boolean;
};

function Block({ block, slug, heroImageUrl, heroVideoUrl, newsEventsContent, resolveCmsHref, onButtonClick, preview }: BlockProps) {
  const cmsClass = blockStyleClass(block);
  const inlineStyle = blockInlineStyle(block);
  if (block.type === "hero") {
    return (
      <section className={["hero", cmsClass].filter(Boolean).join(" ")} style={inlineStyle}>
        <div className="hero-media" aria-hidden="true">
          {heroVideoUrl ? <video src={heroVideoUrl} autoPlay muted loop playsInline /> : heroImageUrl ? <img src={heroImageUrl} alt="" /> : null}
        </div>
        <div className="hero-content">
          {block.eyebrow ? <p className="eyebrow">{block.eyebrow}</p> : null}
          <h1>{block.title}</h1>
          {paragraphs(block.body).map((part) => <p key={part}>{part}</p>)}
          <div className="hero-actions">
            {block.buttons?.map((button, index) => {
              const href = resolveCmsHref(button);
              return (
                <CmsButtonLink key={`${button.href}-${index}`} href={href} variant={button.variant ?? "primary"} openInNewTab={button.openInNewTab ?? isExternalHref(href)} onClick={() => onButtonClick(button, block.title, href)} preview={preview}>
                  {button.label}
                </CmsButtonLink>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  if (block.type === "cta") {
    return (
      <section className={["donation-callout", cmsClass].filter(Boolean).join(" ")} style={inlineStyle}>
        <div className="container donation-inner">
          <div>
            {block.eyebrow ? <p className="eyebrow">{block.eyebrow}</p> : null}
            <h2>{block.title}</h2>
            {paragraphs(block.body).map((part) => <p key={part}>{part}</p>)}
          </div>
          {block.buttons?.[0] ? (
            (() => {
              const button = block.buttons![0];
              const href = resolveCmsHref(button);
              return (
                <CmsButtonLink href={href} variant={button.variant ?? "gold"} openInNewTab={button.openInNewTab ?? isExternalHref(href)} onClick={() => onButtonClick(button, block.title, href)} preview={preview}>
                  {button.label}
                </CmsButtonLink>
              );
            })()
          ) : null}
        </div>
      </section>
    );
  }

  return (
    <Section eyebrow={block.eyebrow} title={block.title} className={[block.className || "", cmsClass].filter(Boolean).join(" ") || undefined} style={inlineStyle}>
      {block.type === "text" ? (
        <div className="cms-text-body">
          {paragraphs(block.body).map((part, index) => <p key={part} className={index === 0 ? "lead" : undefined}>{part}</p>)}
        </div>
      ) : null}
      {block.type === "cards" ? (
        <div className="card-grid">
          {block.cards?.map((card, index) => (
            card.href ? (
              <CmsLink className="card linked-card" key={`${card.title}-${index}`} href={card.href} preview={preview}>
                {card.imageUrl ? <img className="cms-card-image" src={card.imageUrl} alt={card.imageAlt || card.title || ""} loading="lazy" /> : null}
                <h3>{card.title}</h3>
                <p>{card.text}</p>
                <span>Learn more</span>
              </CmsLink>
            ) : (
              <article className="card" key={`${card.title}-${index}`}>
                {card.imageUrl ? <img className="cms-card-image" src={card.imageUrl} alt={card.imageAlt || card.title || ""} loading="lazy" /> : null}
                <h3>{card.title}</h3>
                <p>{card.text}</p>
              </article>
            )
          ))}
        </div>
      ) : null}
      {block.type === "steps" ? (
        <>
          <div className="steps">
            {block.cards?.map((card, index) => (
              <article key={`${card.title}-${index}`}>
                <span>{index + 1}</span>
                <h3>{card.title}</h3>
                <p>{card.text}</p>
              </article>
            ))}
          </div>
          {renderButtons(block, resolveCmsHref, onButtonClick, preview)}
        </>
      ) : null}
      {block.type === "links" ? (
        <div className="quick-links">
          {block.cards?.map((card, index) => (
            <CmsLink key={`${card.title}-${index}`} href={card.href || "/"} preview={preview}>
              {card.title}
            </CmsLink>
          ))}
        </div>
      ) : null}
      {block.type === "newsEvents" ? newsEventsContent ?? <NewsEventsBlock preview={preview} /> : null}
      {block.type === "campaignDashboard" ? <CampaignDashboard campaignId={block.campaignId} dashboardSlug={slug} title={block.title} preview={preview} /> : null}
      {block.type === "video" ? (
        <>
          {paragraphs(block.body).map((part, index) => <p key={part} className={index === 0 ? "lead" : undefined}>{part}</p>)}
          <VideoBlock block={block} />
        </>
      ) : null}
      {block.type === "imageText" ? <ImageTextBlock block={block} resolveCmsHref={resolveCmsHref} onButtonClick={onButtonClick} preview={preview} /> : null}
      {block.type === "gallery" ? (
        <>
          {paragraphs(block.body).map((part, index) => <p key={part} className={index === 0 ? "lead" : undefined}>{part}</p>)}
          <GalleryBlock block={block} />
        </>
      ) : null}
      {block.type === "slideshow" ? (
        <>
          {paragraphs(block.body).map((part, index) => <p key={part} className={index === 0 ? "lead" : undefined}>{part}</p>)}
          <SlideshowBlock block={block} preview={preview} />
        </>
      ) : null}
    </Section>
  );
}

type CmsPageBodyProps = {
  page: CmsPageType;
  slug: string;
  preview?: boolean;
  includeRequestInfo?: boolean;
  newsEventsContent?: ReactNode;
  resolveCmsHref?: ResolveCmsHref;
  onButtonClick?: TrackCmsButton;
  onRequestInfoClick?: TrackRequestInfo;
};

export function CmsPageBody({
  page,
  slug,
  preview = false,
  includeRequestInfo = true,
  newsEventsContent,
  resolveCmsHref = defaultResolveCmsHref,
  onButtonClick = noopTrackButton,
  onRequestInfoClick = noopTrackRequestInfo,
}: CmsPageBodyProps) {
  const hasHeroBlock = page.blocks.some((block) => block.type === "hero");
  const hasPageHeroMedia = Boolean(page.heroImageUrl || page.heroVideoUrl);
  return (
    <>
      {hasPageHeroMedia && !hasHeroBlock ? <PagePhotoHero page={page} /> : null}
      {page.blocks.map((block, index) => {
        const body = (
          <Block
            key={block.id}
            block={block}
            slug={slug}
            heroImageUrl={page.heroImageUrl}
            heroVideoUrl={page.heroVideoUrl}
            newsEventsContent={newsEventsContent}
            resolveCmsHref={resolveCmsHref}
            onButtonClick={onButtonClick}
            preview={preview}
          />
        );
        return preview ? (
          <div key={block.id} className="cms-preview-block" data-block-index={index}>
            {body}
          </div>
        ) : (
          body
        );
      })}
      {includeRequestInfo ? <RequestInfoBand slug={slug} onRequestInfoClick={onRequestInfoClick} preview={preview} /> : null}
    </>
  );
}
